import { SettingsService } from 'rainbow-web-sdk/services/core/settings.service';
import { Service } from 'rainbow-web-sdk/services/service';
import { Subject } from 'rxjs';
import { LogEntry } from '../../models';
import { Strophe } from 'strophe.js';

/**
 * @internal
 */
export const LOGGER_SVC = 'LoggerService';

export interface ExternalLoggerInterface {
    error: (message: string, ...args: unknown[]) => void;
    warn: (message: string, ...args: unknown[]) => void;
    info: (message: string, ...args: unknown[]) => void;
    infoBlue: (message: string, ...args: unknown[]) => void;
    infoEvent: (message: string, ...args: unknown[]) => void;
    infoUser: (message: string, ...args: unknown[]) => void;
    infoOrange: (message: string, ...args: unknown[]) => void;
    log: (message: string, ...args: unknown[]) => void;
    debug: (message: string, ...args: unknown[]) => void;
    webrtc: (message: string, ...args: unknown[]) => void;
    sdk: (message: string, ...args: unknown[]) => void;
    sdkError: (message: string, ...args: unknown[]) => void;
}

export const LogLevelColor = Object.freeze({
    trace: 'color:blue',
    debug: 'color:gray',
    info: 'color:teal',
    infoUser: 'color:darkslateblue',
    infoEvent: 'color:darkslategrey',
    debugEvent: 'color:darkslategrey',
    infoBlue: 'color:white; background: blue',
    infoRed: 'color:white; background: red',
    infoOrange: 'color:black; background: orange',
    log: 'color:teal',
    warn: 'color:red',
    error: 'color:red',
    webrtc: 'color:green'
});

export enum LogLevelEnum {
    ERROR = 0,
    WARNING = 1,
    INFO = 2,
    DEBUG = 3
}

type LogLevel = keyof typeof LogLevelColor;

export class LoggerService extends Service implements ExternalLoggerInterface {
    /**
    * @internal
    */
    public enableConsoleLog: boolean = false;
    public logLevel: LogLevelEnum = LogLevelEnum.DEBUG;
    public logsBufferSize: number = 6000;
    public readonly events = new Subject<LogEntry>();
    private logsBuffer: string[] = [];

    private desktopMode: boolean = false;
    private externalLogger?: ExternalLoggerInterface;
    private readonly settingsService: SettingsService = SettingsService.getInstance();

    /**
     * @internal
     */
    public static getInstance() {
        return Service.create(LOGGER_SVC, LoggerService);
    }

    /**
     * @internal
     */
    public static build() {
        return new LoggerService();
    }

    private constructor() {
        super();
        this.computeConsoleStatus();
        this.settingsService.events('SETTINGS_UPDATED').subscribe(() => { this.computeConsoleStatus(); });
        const ua = navigator.userAgent.toLowerCase();
        this.desktopMode = ua.indexOf("qtwebengine") > -1 || ua.indexOf('electron/') > -1;
    }

    /**
     * @internal
     */
    public computeConsoleStatus(): void {
        if (this.settingsService.getSetting('fullDebugMode')) { this.logsBufferSize = 12000; }
        else { this.logsBufferSize = 6000; }

        //clear the buffer just in case
        while (this.logsBuffer.length >= this.logsBufferSize) { this.logsBuffer.shift(); }

        if (this.settingsService.getSetting('forceConsoleLog') === 'true' || localStorage.getItem('webinarOptionAutolog') === 'true') { this.enableConsoleLog = true; }
        else this.enableConsoleLog = ((window as any).config && (window as any).config.consoleLog) || false;
    }

    /**
     * @internal
     */
    public configure<T extends ExternalLoggerInterface>(externalLogger: T) { this.externalLogger = externalLogger; }

    public static makeObscure(str: string): string {
        if (str.length < 2) return str;
        return str[0] + "***" + str[str.length - 1];
    }

    public error(message: string, ...args: unknown[]): void {
        if (this.externalLogger) { this.externalLogger.error(message); }
        else { this.logger('error', message, ...args); }
    }

    public warn(message: string, ...args: unknown[]): void {
        if (this.externalLogger) { this.externalLogger.info(message); }
        else { this.logger('warn', message, ...args); }
    }

    public info(message: string, ...args: unknown[]): void {
        if (this.externalLogger) { this.externalLogger.info(message); }
        else { this.logger('info', message, ...args); }
    }

    public log(message: string, ...args: unknown[]): void {
        if (this.externalLogger) { this.externalLogger.debug(message, ...args); }
        else { this.logger('debug', message, ...args); }
    }

    public debug(message: string, ...args: unknown[]): void {
        if (this.externalLogger) { this.externalLogger.debug(message, args); }
        else { this.logger('debug', message); }
    }

    /**
     * @internal
     */
    public infoEvent(message: string, ...args: unknown[]): void {
        if (this.externalLogger) { this.externalLogger.info(message, args); }
        else { this.logger('infoEvent', message, ...args); }
    }

    /**
     * @internal
     */
    public debugEvent(message: string, ...args: unknown[]): void {
        if (this.externalLogger) { this.externalLogger.debug(message, args); }
        else { this.logger('debugEvent', message, ...args); }
    }

    /**
     * @internal
     */
    public infoUser(message: string, ...args: unknown[]): void {
        if (this.externalLogger) { this.externalLogger.info(message, args); }
        else { this.logger('infoUser', message, ...args); }
    }

    /**
     * @internal
     */
    public infoBlue(message: string, ...args: unknown[]): void {
        if (this.externalLogger) { this.externalLogger.info(message, args); }
        else { this.logger('infoBlue', message, ...args); }
    }

    /**
     * @internal
     */
    public infoOrange(message: string, ...args: unknown[]): void {
        if (this.externalLogger) { this.externalLogger.info(message, args); }
        else { this.logger('infoOrange', message, ...args); }
    }

    /**
     * @internal
     */
    public infoRed(message: string, ...args: unknown[]): void {
        if (this.externalLogger) { this.externalLogger.info(message, ...args); }
        else { this.logger('infoRed', message, ...args); }
    }

    /**
     * @internal
     */
    public webrtc(message: string, ...args: unknown[]): void {
        if (this.externalLogger) { this.externalLogger.webrtc(message, args); }
        else { this.logger('info', message, ...args); }
    }

    /**
     * @internal
     */
    public sdk(message: string, ...args: unknown[]): void {
        if (this.externalLogger) { this.externalLogger.webrtc(message, ...args); }
        else { this.logger('info', `[SDK] ${message}`); }
    }

    /**
     * @internal
     */
    public sdkError(message: string, ...args: unknown[]): void {
        if (this.externalLogger) { this.externalLogger.webrtc(message, ...args); }
        else { this.logger('error', `[SDK] ${message}`, ...args); }
    }

    /**
     * @internal
     */
    public getLogs(): string[] { return this.logsBuffer; }

    /**
     * @internal
     */
    public logger(level: LogLevel, text: string, ...args: unknown[]) {

        // Compute real level
        const realLevel = this.getRealLevel(level);

        // Filter logs
        const logLevelEnum = this.getLogLevelEnum(realLevel);
        if (logLevelEnum > this.logLevel) return;
        const browserConsole: Console = console;
        const tzoffset = (new Date()).getTimezoneOffset() * 60000; //offset in milliseconds
        const localISOTime = (new Date(Date.now() - tzoffset)).toISOString().slice(0, -1);

        if ((window as any).config && (window as any).config?.telemetry) {
            if (text.startsWith(`[WebinarService`)) {
                const first = args[0];
                let rawData = undefined;
                let rawDataType = undefined;
                if (first && typeof first === "object" && "data" in first) {
                    rawDataType = first['type'];
                    rawData = first['data'];
                    if (rawDataType === 'stanza') { rawDataType = 'xml'; rawData = Strophe.serialize(rawData); }
                }
                this.events.next(LogEntry.create(localISOTime, realLevel, text, rawDataType, rawData));
            }
        }
        args = args.filter(arg => !arg["type"]);
        args = args.filter(arg => !arg["data"]);

        if (this.desktopMode) { browserConsole[realLevel](`${realLevel.toUpperCase()} ${text}`); }
        else {
            const message = `${localISOTime} ${realLevel.toUpperCase()} ${text}`;
            if (this.enableConsoleLog) {
                browserConsole[realLevel](`%c ${message}`, LogLevelColor[level], ...args);
            }
            if (this.logsBuffer.length === this.logsBufferSize) { this.logsBuffer.shift(); }
            this.logsBuffer.push(message + '\n');
        }
    }

    public db(level: LogLevel, module: string, method: string, params: Record<string, any>, details?: string): void {
        const logLevel = this.getRealLevel(level);
        const localISOTime = (new Date(Date.now() - (new Date()).getTimezoneOffset() * 60000)).toISOString().slice(0, -1);
        this.events.next(LogEntry.manualCreate(localISOTime, logLevel, module, method, params, details));
    }

    private getRealLevel(level: LogLevel): string {
        switch (level) {
            case 'infoRed': case 'infoOrange': case 'infoBlue': case 'infoEvent': case 'infoUser': return 'info';
            case 'debugEvent': return 'debug';
            default: return level;
        }
    }

    private getLogLevelEnum(level: string): LogLevelEnum {
        switch (level) {
            case 'debug': case 'log': case 'trace': return LogLevelEnum.DEBUG;
            case 'info': return LogLevelEnum.INFO;
            case 'warning': return LogLevelEnum.WARNING;
            default: return LogLevelEnum.ERROR;
        }
    }

    // ----------------------------------------------------
    //                  QA Auto testing logs APIs
    // ----------------------------------------------------

    /**
     * Clean the log buffer.
     */
    public clearLogs(): void {
        this.logsBuffer = [];
    }

    /**
     * Save the log buffer on disk in the default 'download' fodler.
     * @param custoFilename - User custom file name that will contain all logs present in the buffer.
     */
    public saveLogs(custoFilename?: string): void {
        let fileName: string;
        if (custoFilename?.length) {
            fileName = custoFilename;
        } else {
            fileName = "Rainbow-" + moment().format("DD-MM-YYYY--H-mm-ss") + ".log";
        }
        const content = this.getLogs();
        const blobObject = new Blob(content, { type: "text/plain" });
        this.saveBlobToDisk(blobObject, fileName);
    }

    /**
     * Save raw data from a Blob on disk in the default 'download' fodler.
     * @param blob - Raw data as Blob buffer
     * @param custoFilename - User custom file name that will contain the raw data.
     */
    private saveBlobToDisk(blob: Blob, filename: string): void {
        // Create a temporary URL for the blob
        const dummyUrl = URL.createObjectURL(blob);

        //  Create a download link
        const dummyLink = document.createElement('a');
        dummyLink.href = dummyUrl;
        dummyLink.download = filename;

        // Process the download
        document.body.appendChild(dummyLink);
        dummyLink.click();

        // Cleaning
        document.body.removeChild(dummyLink);
        URL.revokeObjectURL(dummyUrl);
    }

}
